import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { IconSparkles } from '@tabler/icons-react';

import CustomDialog from '../modals/CustomDialog';
import Button from '../buttons/Button';
import Input from '../inputs/Input';
import TextareaWithCounter from '../inputs/TextareaWithCounter';
import { TAG_DESCRIPTION_MAX_LENGTH } from '../../core/constants';

/**
 * Create a custom tag (visual spec: option C2 in
 * .kiro/specs/contact-grouping/mockups.html).
 *
 * The description is audience context handed to AI content generation, not a note
 * to self — and a plain required textarea reliably yields "my donors", which is
 * worse than a missing description because it silently degrades every generated
 * piece instead of failing visibly. Two things are doing the work here, and both
 * are framing rather than validation:
 *
 *   1. The label is a QUESTION, not the noun "Description".
 *   2. A preview panel shows the text in the role it will actually play, so a
 *      two-word answer visibly looks wrong before it is saved.
 */
const MIN_USEFUL_DESCRIPTION_LENGTH = 40;

const TagFormModal = ({ open, onClose, onSave, loading, mode, initialName, initialDescription, isReserved, error }) => {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [submitted, setSubmitted] = useState(false);

  const isEdit = mode === 'edit';

  // Keyed remount would also work, but the modal is reused from three entry points
  // (the Tags page, the wizard's Tag step, and the wizard's tag resolution), so
  // seed on open.
  useEffect(() => {
    if (open) {
      setName(initialName);
      setDescription(initialDescription);
      setSubmitted(false);
    }
  }, [open, initialName, initialDescription]);

  const trimmedName = name.trim();
  const trimmedDescription = description.trim();

  const nameError = submitted && !trimmedName;
  const descriptionError = submitted && (!trimmedDescription || trimmedDescription.length > TAG_DESCRIPTION_MAX_LENGTH);

  const descriptionHelper = useMemo(() => {
    if (descriptionError && !trimmedDescription) return 'A description is required.';
    if (trimmedDescription.length > TAG_DESCRIPTION_MAX_LENGTH) return 'This is too long.';
    if (trimmedDescription.length >= MIN_USEFUL_DESCRIPTION_LENGTH) {
      return 'Good detail — says who they are and the tone to use.';
    }
    if (trimmedDescription) return 'Add a little more so we know how to write to them.';
    return 'Used to guide content writing for this group.';
  }, [descriptionError, trimmedDescription]);

  const helperClassName =
    descriptionError || trimmedDescription.length > TAG_DESCRIPTION_MAX_LENGTH
      ? 'text-red-500 font-semibold'
      : trimmedDescription.length >= MIN_USEFUL_DESCRIPTION_LENGTH
        ? 'text-green-600 font-semibold'
        : 'text-gray-600';

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const handleSave = () => {
    setSubmitted(true);
    if (!trimmedName || !trimmedDescription) return;
    if (trimmedDescription.length > TAG_DESCRIPTION_MAX_LENGTH) return;
    // Omit the name for a reserved tag rather than sending it unchanged — the API
    // rejects any name key on those, even an identical one.
    onSave(isReserved ? { description: trimmedDescription } : { name: trimmedName, description: trimmedDescription });
  };

  return (
    <CustomDialog
      open={open}
      onClose={handleClose}
      title={isEdit ? 'Edit Tag' : 'Create Tag'}
      containerClassName="sm:min-w-[480px] sm:max-w-[480px]"
      titleClassName="!text-2xl"
      closeDisabled={loading}
    >
      {isReserved && (
        <p className="mt-1 text-sm text-gray-600">
          This tag is managed by DonorSpring. You can refine how content is written for it, but its name is fixed.
        </p>
      )}

      <Input
        id="tag-name"
        label="Tag name"
        value={name}
        onChange={(event) => setName(event.target.value)}
        required
        // Renaming a reserved tag is refused with a 403: seeding keys on the name
        // and the onboarding donor cards resolve the reserved tags by it.
        readOnly={isReserved}
        error={nameError}
        helperText={nameError ? 'A tag name is required.' : ''}
        containerClassName="mt-5"
      />

      <TextareaWithCounter
        id="tag-description"
        label="Who is in this group, and how should we write to them?"
        value={description}
        onChange={(event) => setDescription(event.target.value)}
        maxLength={TAG_DESCRIPTION_MAX_LENGTH}
        required
        rows={4}
        error={descriptionError}
        helperText={descriptionHelper}
        helperClassName={helperClassName}
        containerClassName="mt-5"
      />

      {!!trimmedDescription && (
        <div className="mt-4 rounded-lg bg-purple-50 px-3.5 py-3">
          <div className="mb-1 flex items-center gap-1.5">
            <IconSparkles size={14} className="text-purple-500" />
            <span className="text-caption1 font-semibold uppercase tracking-wide text-purple-500">
              How we&apos;ll use this
            </span>
          </div>
          <p className="text-sm text-gray-700">
            When drafting content for <span className="font-semibold">{trimmedName || 'this tag'}</span>, we&apos;ll
            tell the writer: <span className="italic">&ldquo;{trimmedDescription}&rdquo;</span>
          </p>
        </div>
      )}

      {!!error && <p className="mt-4 text-sm font-semibold text-red-500">{error}</p>}

      <div className="mt-6 flex justify-end space-x-3">
        <Button title="Cancel" onClick={handleClose} disabled={loading} />
        <Button
          title={isEdit ? 'Save' : 'Create'}
          color="primary"
          onClick={handleSave}
          disabled={loading}
          loading={loading}
        />
      </div>
    </CustomDialog>
  );
};

TagFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  mode: PropTypes.oneOf(['create', 'edit']),
  initialName: PropTypes.string,
  initialDescription: PropTypes.string,
  isReserved: PropTypes.bool,
  error: PropTypes.string,
};

TagFormModal.defaultProps = {
  loading: false,
  mode: 'create',
  initialName: '',
  initialDescription: '',
  isReserved: false,
  error: '',
};

export default TagFormModal;
